export type TcoAssumptions = {
  fleetSize: number;
  years: number;
};

export type TcoScenario = {
  unitPrice: number;
  logisticsPerDevice: number;
  oneTimeSetup: number;
  annualFailureRatePct: number;
  replacementIncidentCost: number;
  annualSupportContactsPer1000: number;
  supportContactCost: number;
  annualTruckRollsPer1000: number;
  truckRollCost: number;
  dmsPerDevicePerMonth: number;
};

export type TcoBreakdown = {
  procurement: number;
  logistics: number;
  setup: number;
  replacements: number;
  support: number;
  fieldService: number;
  management: number;
};

export type TcoResult = {
  breakdown: TcoBreakdown;
  total: number;
  perDevice: number;
  perDevicePerMonth: number;
};

export const DEFAULT_TCO_ASSUMPTIONS: TcoAssumptions = {
  fleetSize: 10_000,
  years: 4,
};

export const DEFAULT_TCO_SCENARIOS: readonly [TcoScenario, TcoScenario] = [
  {
    unitPrice: 40,
    logisticsPerDevice: 3,
    oneTimeSetup: 12_000,
    annualFailureRatePct: 3,
    replacementIncidentCost: 55,
    annualSupportContactsPer1000: 300,
    supportContactCost: 6,
    annualTruckRollsPer1000: 30,
    truckRollCost: 80,
    dmsPerDevicePerMonth: 0.15,
  },
  {
    unitPrice: 44,
    logisticsPerDevice: 3,
    oneTimeSetup: 18_000,
    annualFailureRatePct: 1.5,
    replacementIncidentCost: 55,
    annualSupportContactsPer1000: 150,
    supportContactCost: 6,
    annualTruckRollsPer1000: 8,
    truckRollCost: 80,
    dmsPerDevicePerMonth: 0.25,
  },
];

function nonNegative(value: number): number {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

export function calculateTco(assumptions: TcoAssumptions, scenario: TcoScenario): TcoResult {
  const fleetSize = nonNegative(assumptions.fleetSize);
  const years = nonNegative(assumptions.years);
  const fleetThousands = fleetSize / 1_000;

  const breakdown: TcoBreakdown = {
    procurement: fleetSize * nonNegative(scenario.unitPrice),
    logistics: fleetSize * nonNegative(scenario.logisticsPerDevice),
    setup: nonNegative(scenario.oneTimeSetup),
    replacements:
      fleetSize *
      (nonNegative(scenario.annualFailureRatePct) / 100) *
      years *
      nonNegative(scenario.replacementIncidentCost),
    support:
      fleetThousands *
      nonNegative(scenario.annualSupportContactsPer1000) *
      years *
      nonNegative(scenario.supportContactCost),
    fieldService:
      fleetThousands *
      nonNegative(scenario.annualTruckRollsPer1000) *
      years *
      nonNegative(scenario.truckRollCost),
    management: fleetSize * nonNegative(scenario.dmsPerDevicePerMonth) * years * 12,
  };

  const total = Object.values(breakdown).reduce((sum, value) => sum + value, 0);
  const months = years * 12;

  return {
    breakdown,
    total,
    perDevice: fleetSize > 0 ? total / fleetSize : 0,
    perDevicePerMonth: fleetSize > 0 && months > 0 ? total / fleetSize / months : 0,
  };
}
